/**
 * @file timesheetCalc.js
 * @description Pure, unit-tested calculation engine for the Timesheet Tracker.
 *   All functions here are side-effect free and take plain data (the arrays of
 *   tt_* records, already loaded for the current user) so they can be tested in
 *   isolation. No Supabase, no React.
 *
 *   These are the trust-critical calcs — money and dates. Rules:
 *     • income   = duration_minutes / 60 × rate_snapshot, rounded to cents
 *     • units    = duration_minutes / 15
 *     • a date maps to a pay cycle via:
 *         cycleIndex  = floor((entryDate − anchor) / cycleLength)
 *         periodStart = anchor + cycleIndex × cycleLength
 *         periodEnd   = periodStart + (cycleLength − 1)
 *         payDate     = periodEnd + payDelayDays
 *     • cycle income = Σ income of billable entries in [periodStart, periodEnd]
 *     • net          = income − agency deductions + reimbursable expenses
 *     • outstanding  = Σ (net − actualReceived) over cycles with payDate ≤ today
 *
 *   Records use the snake_case shape returned by Supabase (e.g. `duration_minutes`,
 *   `agency_id`, `period_start`). Dates are YYYY-MM-DD strings compared
 *   lexicographically — valid because the format sorts chronologically.
 *
 * @module lib/timesheetCalc
 */

import {
  roundMoney,
  addDaysToDateKey,
  dateKeyToDate,
  getDateKeyDifference,
  todayKey,
  toDateKey,
  startOfWeek,
  addDays,
  sum,
  groupBy,
} from "./timesheetTime";
import { rollToPriorBusinessDay } from "./businessDays";

// Required weekly services for coverage alerts. Matched case-insensitively
// against the user's tt_services names. "Behavior Treatment" maps to CPT 97155,
// "Parent Training" to 97156 (per authorizations.js); "Family Training" is
// accepted as an alias for the parent-training requirement.
export const REQUIRED_WEEKLY_SERVICES = ["Behavior Treatment", "Parent Training"];
const REQUIRED_ALIASES = {
  "behavioral treatment": "Behavior Treatment",
  "behavior treatment": "Behavior Treatment",
  "family training": "Parent Training",
  "parent training": "Parent Training",
};

function paymentConfigFor(agency) {
  if (!agency) return null;
  return {
    enabled: !!agency.payment_enabled,
    anchorPeriodStart: agency.anchor_period_start || "",
    cycleLengthDays: Number(agency.cycle_length_days) || 0,
    payDelayDays: Number(agency.pay_delay_days) || 0,
    periodType: agency.pay_period_type || "fixed_days",
    payWeekday: agency.pay_weekday == null ? null : Number(agency.pay_weekday),
    payOnBusinessDays: agency.pay_on_business_days !== false,
    payDateRule: agency.pay_date_rule || "delay_weekday",
    payAnchorDate: agency.pay_anchor_date || "",
    payCadenceDays: Number(agency.pay_cadence_days) || 0,
  };
}

export function paymentCycleKey(agencyId, periodStart, periodEnd) {
  return `${agencyId}:${periodStart}:${periodEnd}`;
}

/**
 * Resolve the pay schedule effective on a given date, honoring effective-dated
 * history. `pay_schedule_history` holds PAST segments, each with an
 * [effective_from, effective_to) range (effective_to exclusive); a date inside a
 * segment uses that segment, otherwise the current columns apply. Empty history
 * (the default) means the current columns apply to every date — identical to the
 * pre-history behavior.
 *
 * @returns config in paymentConfigFor's shape, or null.
 */
export function scheduleForDate(agency, dateKey) {
  if (!agency) return null;
  const history = Array.isArray(agency.pay_schedule_history) ? agency.pay_schedule_history : [];
  for (const seg of history) {
    if (!seg || !seg.effective_to) continue;
    const afterFrom = !seg.effective_from || dateKey >= seg.effective_from;
    const beforeTo = dateKey < seg.effective_to;
    if (afterFrom && beforeTo) {
      return {
        enabled: true,
        anchorPeriodStart: seg.anchor_period_start || "",
        cycleLengthDays: Number(seg.cycle_length_days) || 0,
        payDelayDays: Number(seg.pay_delay_days) || 0,
        periodType: seg.pay_period_type || "fixed_days",
        payWeekday: seg.pay_weekday == null ? null : Number(seg.pay_weekday),
        payOnBusinessDays: seg.pay_on_business_days == null
          ? agency.pay_on_business_days !== false
          : seg.pay_on_business_days !== false,
        payDateRule: seg.pay_date_rule || "delay_weekday",
        payAnchorDate: seg.pay_anchor_date || "",
        payCadenceDays: Number(seg.pay_cadence_days) || 0,
      };
    }
  }
  return paymentConfigFor(agency);
}

/**
 * Build the agency-update fields for a pay-schedule change.
 *   - mode "retroactive": the new schedule applies to ALL dates; history cleared.
 *   - mode "future": the current schedule is archived as a closed segment ending
 *     at `effectiveFrom`, and the new schedule takes over from that date forward.
 * Pure — returns a plain patch the caller passes to updateAgency.
 *
 * @param {Object} agency - the agency BEFORE the change (current columns + history)
 * @param {{anchor_period_start, cycle_length_days, pay_delay_days}} newSchedule
 * @param {"retroactive"|"future"} mode
 * @param {string} [effectiveFrom] - YYYY-MM-DD, required for "future"
 */
export function buildScheduleChange(agency, newSchedule, mode, effectiveFrom) {
  const next = {
    anchor_period_start: newSchedule.anchor_period_start || null,
    cycle_length_days: newSchedule.cycle_length_days != null ? Number(newSchedule.cycle_length_days) : null,
    pay_delay_days: newSchedule.pay_delay_days != null ? Number(newSchedule.pay_delay_days) : null,
    pay_period_type: newSchedule.pay_period_type || "fixed_days",
    pay_weekday: newSchedule.pay_weekday == null ? null : Number(newSchedule.pay_weekday),
    pay_on_business_days: newSchedule.pay_on_business_days !== false,
    pay_date_rule: newSchedule.pay_date_rule || "delay_weekday",
    pay_anchor_date: newSchedule.pay_anchor_date || null,
    pay_cadence_days: newSchedule.pay_cadence_days == null ? null : Number(newSchedule.pay_cadence_days),
  };
  if (mode !== "future" || !effectiveFrom) {
    return { ...next, pay_schedule_history: [] };
  }
  const history = Array.isArray(agency.pay_schedule_history) ? agency.pay_schedule_history : [];
  const currentFrom = history.length
    ? history.reduce((mx, s) => (s.effective_to > mx ? s.effective_to : mx), history[0].effective_to)
    : null;
  // Guard: a future change must start after the current schedule began.
  if (currentFrom && effectiveFrom <= currentFrom) {
    return { ...next, pay_schedule_history: history };
  }
  const archived = {
    effective_from: currentFrom,
    effective_to: effectiveFrom,
    anchor_period_start: agency.anchor_period_start || null,
    cycle_length_days: agency.cycle_length_days != null ? Number(agency.cycle_length_days) : null,
    pay_delay_days: agency.pay_delay_days != null ? Number(agency.pay_delay_days) : null,
    pay_period_type: agency.pay_period_type || "fixed_days",
    pay_weekday: agency.pay_weekday == null ? null : Number(agency.pay_weekday),
    pay_on_business_days: agency.pay_on_business_days !== false,
    pay_date_rule: agency.pay_date_rule || "delay_weekday",
    pay_anchor_date: agency.pay_anchor_date || null,
    pay_cadence_days: agency.pay_cadence_days == null ? null : Number(agency.pay_cadence_days),
  };
  return { ...next, pay_schedule_history: [...history, archived] };
}

/**
 * Map a single entry date to its pay cycle for an agency. Returns null if the
 * agency has no usable payment config. Honors effective-dated schedule history.
 * @returns {{periodStart, periodEnd, payDate, key}|null}
 */
function keyOf(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Calendar period containing dateKey: 1st-15th or 16th-month end. */
export function semiMonthlyPeriodForDate(dateKey) {
  const d = dateKeyToDate(dateKey);
  if (!d || Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  if (d.getDate() <= 15) {
    return { periodStart: keyOf(year, month, 1), periodEnd: keyOf(year, month, 15) };
  }
  const lastDay = new Date(year, month, 0).getDate();
  return { periodStart: keyOf(year, month, 16), periodEnd: keyOf(year, month, lastDay) };
}

/** Roll forward to weekday (0=Sun...6=Sat), keeping the date if it already matches. */
function rollForwardToWeekday(dateKey, weekday) {
  if (weekday == null || weekday < 0 || weekday > 6) return dateKey;
  const date = dateKeyToDate(dateKey);
  const offset = (weekday - date.getDay() + 7) % 7;
  return addDaysToDateKey(dateKey, offset);
}

/** First date in an anchored cadence strictly after the period end. */
export function anchoredPayDateAfter(periodEnd, anchorDate, cadenceDays) {
  const cadence = Number(cadenceDays) || 0;
  if (!periodEnd || !anchorDate || cadence < 1) return null;
  const daysFromAnchor = getDateKeyDifference(anchorDate, periodEnd);
  if (daysFromAnchor == null) return null;
  const cycleIndex = Math.floor(daysFromAnchor / cadence) + 1;
  return addDaysToDateKey(anchorDate, cycleIndex * cadence);
}

function scheduledPayDate(periodEnd, config) {
  if (config.payDateRule === "anchored_cadence") {
    const anchored = anchoredPayDateAfter(periodEnd, config.payAnchorDate, config.payCadenceDays);
    if (anchored) return anchored;
  }
  let payDate = addDaysToDateKey(periodEnd, config.payDelayDays || 0);
  payDate = rollForwardToWeekday(payDate, config.payWeekday);
  if (config.payOnBusinessDays !== false) payDate = rollToPriorBusinessDay(payDate);
  return payDate;
}

export function cycleForDate(agency, dateKey) {
  const cfg = scheduleForDate(agency, dateKey);
  if (!cfg || !cfg.enabled) {
    return null;
  }
  let period;
  if (cfg.periodType === "semi_monthly") {
    period = semiMonthlyPeriodForDate(dateKey);
  } else {
    if (!cfg.anchorPeriodStart || cfg.cycleLengthDays < 1) return null;
    const daysFromAnchor = getDateKeyDifference(cfg.anchorPeriodStart, dateKey);
    if (daysFromAnchor == null) return null;
    const cycleIndex = Math.floor(daysFromAnchor / cfg.cycleLengthDays);
    const periodStart = addDaysToDateKey(cfg.anchorPeriodStart, cycleIndex * cfg.cycleLengthDays);
    period = { periodStart, periodEnd: addDaysToDateKey(periodStart, cfg.cycleLengthDays - 1) };
  }
  if (!period) return null;
  const { periodStart, periodEnd } = period;
  const payDate = scheduledPayDate(periodEnd, cfg);
  return { periodStart, periodEnd, payDate, key: paymentCycleKey(agency.id, periodStart, periodEnd) };
}

/**
 * Preview the next `count` pay dates for a raw schedule config (not an agency
 * row) — used by the setup form so a user can confirm their anchor/cycle/delay
 * produce the dates they expect. Returns upcoming cycles (payDate ≥ today),
 * soonest first.
 *
 * @param {{anchorPeriodStart, cycleLengthDays, payDelayDays}} config
 * @param {number} [count=4]
 * @param {string} [today=todayKey()]
 * @returns {Array<{payDate, periodStart, periodEnd}>}
 */
export function nextPayDates(config, count = 4, today = todayKey()) {
  if (config?.periodType === "semi_monthly") {
    const todayDate = dateKeyToDate(today);
    if (!todayDate) return [];
    const candidates = [];
    for (let monthOffset = -2; monthOffset < 36 && candidates.length < count; monthOffset++) {
      const month = new Date(todayDate.getFullYear(), todayDate.getMonth() + monthOffset, 1);
      const year = month.getFullYear();
      const month1 = month.getMonth() + 1;
      const last = new Date(year, month1, 0).getDate();
      for (const [startDay, endDay] of [[1, 15], [16, last]]) {
        const periodStart = keyOf(year, month1, startDay);
        const periodEnd = keyOf(year, month1, endDay);
        const payDate = scheduledPayDate(periodEnd, config);
        if (payDate >= today) candidates.push({ payDate, periodStart, periodEnd });
      }
    }
    return candidates.sort((a, b) => a.payDate.localeCompare(b.payDate)).slice(0, count);
  }
  const anchor = config?.anchorPeriodStart;
  const cycleLen = Number(config?.cycleLengthDays) || 0;
  const delay = Number(config?.payDelayDays) || 0;
  if (!anchor || cycleLen < 1) return [];
  const daysFromAnchor = getDateKeyDifference(anchor, today);
  if (daysFromAnchor == null) return [];
  // Start a couple cycles before today's so a just-passed-period / upcoming
  // payDate isn't missed, but never before the anchor's first cycle.
  let k = Math.max(0, Math.floor(daysFromAnchor / cycleLen) - 2);
  const out = [];
  let guard = 0;
  while (out.length < count && guard < 1000) {
    guard++;
    const periodStart = addDaysToDateKey(anchor, k * cycleLen);
    const periodEnd = addDaysToDateKey(periodStart, cycleLen - 1);
    const payDate = scheduledPayDate(periodEnd, { ...config, payDelayDays: delay });
    if (payDate >= today) out.push({ payDate, periodStart, periodEnd });
    k++;
  }
  return out;
}

/**
 * Build all payment cycles across every payment-enabled agency from the user's
 * entries, applying any reconciliation overrides (tt_payment_schedules).
 *
 * @param {Object} data - { agencies, timeEntries, expenses, paymentSchedules }
 * @returns {Array} cycles sorted newest payDate first, hidden cycles dropped.
 */
export function buildPaymentCycles(data) {
  const agencies = data.agencies || [];
  const timeEntries = data.timeEntries || [];
  const expenses = data.expenses || [];
  const overrides = data.paymentSchedules || [];
  const paymentRecords = data.paymentRecords || [];

  const agenciesById = Object.fromEntries(agencies.map((a) => [a.id, a]));
  const billable = timeEntries.filter((e) => e.billable !== false);

  // Seed one cycle per (agency, period) that has at least one billable entry.
  const baseCycles = new Map();
  for (const entry of billable) {
    const agency = agenciesById[entry.agency_id];
    const cycle = cycleForDate(agency, entry.date);
    if (!cycle) continue;
    if (!baseCycles.has(cycle.key)) {
      baseCycles.set(cycle.key, {
        key: cycle.key,
        agencyId: entry.agency_id,
        agencyName: agency?.name || "Unknown agency",
        periodStart: cycle.periodStart,
        periodEnd: cycle.periodEnd,
        payDate: cycle.payDate,
        actualReceived: null,
        notes: "",
        hidden: false,
        storageId: null,
      });
    }
  }

  // A reimbursement can be the only amount due in a period (for example,
  // mileage submitted after the last session). Seed that agency's cycle even
  // when no billable entry exists, so the repayment is not hidden.
  for (const expense of expenses.filter((x) => x.is_reimbursable === true && x.agency_id)) {
    const agency = agenciesById[expense.agency_id];
    const cycle = cycleForDate(agency, expense.date);
    if (!cycle || baseCycles.has(cycle.key)) continue;
    baseCycles.set(cycle.key, {
      key: cycle.key,
      agencyId: expense.agency_id,
      agencyName: agency?.name || "Unknown agency",
      periodStart: cycle.periodStart,
      periodEnd: cycle.periodEnd,
      payDate: cycle.payDate,
      actualReceived: null,
      notes: "",
      hidden: false,
      storageId: null,
    });
  }

  const resolved = Array.from(baseCycles.values()).map((base) => {
    const override = overrides.find(
      (o) =>
        o.agency_id === base.agencyId &&
        o.period_start === base.periodStart &&
        o.period_end === base.periodEnd
    );
    const entries = billable.filter(
      (e) =>
        e.agency_id === base.agencyId &&
        cycleForDate(agenciesById[e.agency_id], e.date)?.key === base.key
    );
    // Payment treatment is explicit: agency deductions reduce what is owed,
    // reimbursements increase it, and ordinary out-of-pocket expenses affect
    // expense reporting only.
    const allCycleExpenses = expenses.filter(
      (x) =>
        x.agency_id === base.agencyId &&
        cycleForDate(agenciesById[x.agency_id], x.date)?.key === base.key
    );
    const cycleExpenses = allCycleExpenses.filter((x) => x.is_agency_deducted === true);
    const cycleReimbursements = allCycleExpenses.filter((x) => x.is_reimbursable === true);
    const income = roundMoney(sum(entries, (e) => Number(e.income) || 0));
    const expensesTotal = roundMoney(sum(cycleExpenses, (x) => Number(x.amount) || 0));
    const reimbursementsTotal = roundMoney(sum(cycleReimbursements, (x) => Number(x.amount) || 0));
    const computedNet = roundMoney(income - expensesTotal + reimbursementsTotal);
    const net = override?.net_override != null ? roundMoney(Number(override.net_override)) : computedNet;
    const actualReceived =
      override && override.actual_received != null && override.actual_received !== ""
        ? Number(override.actual_received)
        : null;
    return {
      ...base,
      payDate: override?.pay_date || base.payDate,
      storageId: override?.id || null,
      actualReceived,
      confirmedAt: override?.confirmed_at || null,
      // Installment ledger behind actualReceived (the maintained sum). Empty for
      // cycles with no override row yet. Sorted oldest received first.
      payments: override
        ? paymentRecords
            .filter((r) => r.schedule_id === override.id)
            .slice()
            .sort((a, b) => (a.received_date || "").localeCompare(b.received_date || ""))
        : [],
      notes: override?.notes ?? base.notes,
      hidden: !!override?.hidden,
      income,
      expenses: expensesTotal,
      cycleExpenseItems: cycleExpenses,
      reimbursements: reimbursementsTotal,
      cycleReimbursementItems: cycleReimbursements,
      net,
      netOverride: override?.net_override != null ? roundMoney(Number(override.net_override)) : null,
      entryCount: entries.length,
      isManual: false,
      manualDescription: "",
    };
  });

  // Inject manual payment rows (created directly by user, not derived from entries).
  const manualOverrides = overrides.filter((o) => o.is_manual && !o.hidden);
  for (const m of manualOverrides) {
    const agency = agenciesById[m.agency_id];
    const actualReceived =
      m.actual_received != null && m.actual_received !== ""
        ? Number(m.actual_received)
        : null;
    // A "catch-up" manual payment is cash for work already counted in an earlier
    // cycle — not new income. Net 0 turns it into a credit (received > net) that
    // reconcileOverpayments applies to this agency's shortfall. A normal manual
    // payment (bonus/reimbursement) keeps net = received, so it stays balanced.
    const appliesToBalance = !!(m.agency_id && m.applies_to_balance);
    const net = appliesToBalance ? 0 : (actualReceived ?? 0);
    resolved.push({
      key: `manual:${m.id}`,
      agencyId: m.agency_id || null,
      agencyName: agency?.name || "Unknown agency",
      periodStart: m.period_start || m.pay_date || "",
      periodEnd: m.period_end || m.pay_date || "",
      payDate: m.pay_date || "",
      actualReceived,
      confirmedAt: m.confirmed_at || null,
      notes: m.notes || "",
      hidden: false,
      storageId: m.id,
      income: 0,
      expenses: 0,
      cycleExpenseItems: [],
      reimbursements: 0,
      cycleReimbursementItems: [],
      net,
      entryCount: 0,
      isManual: true,
      appliesToBalance,
      manualDescription: m.manual_description || "",
    });
  }

  return resolved
    .filter((c) => !c.hidden)
    .sort((a, b) => `${b.payDate}-${b.key}`.localeCompare(`${a.payDate}-${a.key}`));
}

/**
 * Per-agency overpayment reconciliation. An agency that underpays one cycle and
 * later makes it up by overpaying another (a "catch-up" payment) should net to
 * zero — not leave the short cycle stuck as overdue forever. We pool each
 * agency's overpayments (cycles where actualReceived > net) and apply them as
 * credit against that SAME agency's shortfall cycles, oldest period first.
 * Credits never cross agencies — one agency's surplus can't mask another's debt.
 *
 * Returns a Map keyed by cycle.key (over the DUE cycles only, payDate ≤ today):
 *   { raw, effective, creditApplied, creditSource }
 *   • raw          — the cycle's own (net − actualReceived)
 *   • effective    — raw after same-agency overpayment credit (never negative)
 *   • creditApplied— how much another cycle's overpayment covered this shortfall
 *   • creditSource — true if this cycle's overpayment funded the credit pool
 *
 * @example A March cycle short $440 and an April cycle over $440 for one agency
 *   both resolve to effective 0 — the agency is square, nothing is overdue.
 */
export function reconcileOverpayments(cycles, today = todayKey()) {
  const due = (cycles || []).filter((c) => c.payDate && c.payDate <= today);
  const byAgency = new Map();
  for (const c of due) {
    const key = c.agencyId || `__solo__:${c.key}`; // null-agency manual rows stay isolated
    if (!byAgency.has(key)) byAgency.set(key, []);
    byAgency.get(key).push(c);
  }
  const out = new Map();
  for (const group of byAgency.values()) {
    const rows = [...group]
      .sort((a, b) => `${a.periodStart}-${a.key}`.localeCompare(`${b.periodStart}-${b.key}`))
      .map((c) => ({ c, raw: roundMoney((c.net || 0) - (c.actualReceived || 0)) }));
    // Pool every overpayment for this agency, then spend it on shortfalls oldest-first.
    let credit = roundMoney(rows.reduce((s, r) => s + (r.raw < -0.005 ? -r.raw : 0), 0));
    for (const r of rows) {
      if (r.raw < -0.005) {
        out.set(r.c.key, { raw: r.raw, effective: 0, creditApplied: 0, creditSource: true });
      } else if (r.raw > 0.005 && credit > 0.005) {
        const applied = Math.min(r.raw, credit);
        credit = roundMoney(credit - applied);
        out.set(r.c.key, { raw: r.raw, effective: roundMoney(r.raw - applied), creditApplied: applied, creditSource: false });
      } else {
        out.set(r.c.key, { raw: r.raw, effective: r.raw, creditApplied: 0, creditSource: false });
      }
    }
  }
  return out;
}

function recFor(rec, c) {
  return rec.get(c.key) || {
    raw: roundMoney((c.net || 0) - (c.actualReceived || 0)),
    effective: roundMoney((c.net || 0) - (c.actualReceived || 0)),
    creditApplied: 0,
    creditSource: false,
  };
}

/**
 * Outstanding balance = Σ effective balance over cycles whose payDate has passed
 * (≤ today), after per-agency overpayment reconciliation. Equivalent to the sum
 * over agencies of max(0, what that agency still owes). A cycle with
 * actualReceived null counts its full net as outstanding.
 */
export function outstandingBalance(cycles, today = todayKey()) {
  const rec = reconcileOverpayments(cycles, today);
  const due = (cycles || []).filter((c) => c.payDate && c.payDate <= today);
  return roundMoney(sum(due, (c) => recFor(rec, c).effective));
}

/** Cycles due today (payDate === today) that still owe money. */
export function dueTodayCycles(cycles, today = todayKey()) {
  const rec = reconcileOverpayments(cycles, today);
  return (cycles || [])
    .filter((c) => c.payDate && c.payDate === today)
    .map((c) => {
      const r = recFor(rec, c);
      return { ...c, balance: r.effective, rawBalance: r.raw, creditApplied: r.creditApplied, creditSource: r.creditSource };
    })
    .filter((c) => c.balance > 0.005);
}

/** Cycles that are strictly past due (payDate < today) and still owe money after reconciliation. */
export function overdueCycles(cycles, today = todayKey()) {
  const rec = reconcileOverpayments(cycles, today);
  return (cycles || [])
    .filter((c) => c.payDate && c.payDate < today)
    .map((c) => {
      const r = recFor(rec, c);
      return { ...c, balance: r.effective, rawBalance: r.raw, creditApplied: r.creditApplied, creditSource: r.creditSource };
    })
    .filter((c) => c.balance > 0.005);
}

/**
 * Upcoming cycles (payDate in the future) — money you expect but isn't owed yet.
 * Includes cycles that have been PARTIALLY paid early (some installments recorded
 * but a balance remains) so a cycle you're still owed on never disappears. A
 * future cycle paid in full falls through to Settled instead.
 */
export function upcomingCycles(cycles, today = todayKey()) {
  const rec = reconcileOverpayments(cycles, today);
  return (cycles || [])
    .filter((c) => c.payDate && c.payDate > today)
    .map((c) => {
      const r = recFor(rec, c);
      return { ...c, balance: r.effective, rawBalance: r.raw, creditApplied: r.creditApplied, creditSource: r.creditSource };
    })
    .filter((c) => c.balance > 0.005);
}

/**
 * Settled cycles: a payment was recorded and after per-agency overpayment
 * reconciliation the cycle owes nothing (effective ≤ $0). Includes early
 * payments (future pay date) and cycles whose balance was cleared by an
 * overpayment from another cycle of the same agency.
 */
export function settledCycles(cycles, today = todayKey()) {
  const rec = reconcileOverpayments(cycles, today);
  return (cycles || [])
    .filter((c) => c.payDate && c.actualReceived != null)
    .map((c) => {
      const r = recFor(rec, c);
      return { ...c, balance: r.effective, rawBalance: r.raw, creditApplied: r.creditApplied, creditSource: r.creditSource };
    })
    .filter((c) => c.balance <= 0.005)
    .sort((a, b) => b.payDate.localeCompare(a.payDate));
}

/**
 * Month-to-date metrics for the given month prefix (YYYY-MM, defaults to the
 * current month). Returns income, expenses, net, minutes, billable minutes,
 * and a per-service income breakdown.
 */
export function buildMonthlyMetrics(data, monthPrefix) {
  const timeEntries = data.timeEntries || [];
  const expenses = data.expenses || [];
  const services = data.services || [];
  const prefix = monthPrefix || todayKey().slice(0, 7);

  const monthEntries = timeEntries.filter((e) => (e.date || "").startsWith(prefix));
  const monthExpenses = expenses.filter((x) => (x.date || "").startsWith(prefix));
  const income = roundMoney(sum(monthEntries, (e) => Number(e.income) || 0));
  const expensesTotal = roundMoney(sum(monthExpenses, (x) => Number(x.amount) || 0));
  const minutes = sum(monthEntries, (e) => Number(e.duration_minutes) || 0);
  const billableMinutes = sum(
    monthEntries.filter((e) => e.billable !== false),
    (e) => Number(e.duration_minutes) || 0
  );

  const serviceMap = Object.fromEntries(services.map((s) => [s.id, s]));
  const byService = groupBy(monthEntries, (e) => e.service_id);
  const serviceBreakdown = Object.entries(byService)
    .map(([serviceId, items]) => ({
      serviceId,
      name: serviceMap[serviceId]?.name || "Service",
      minutes: sum(items, (i) => Number(i.duration_minutes) || 0),
      income: roundMoney(sum(items, (i) => Number(i.income) || 0)),
    }))
    .sort((a, b) => b.income - a.income);

  return {
    monthPrefix: prefix,
    income,
    expenses: expensesTotal,
    net: roundMoney(income - expensesTotal),
    minutes,
    billableMinutes,
    serviceBreakdown,
  };
}

/** Year-to-date earnings (sum of entry income for the given year, default current). */
export function ytdEarnings(data, year) {
  const timeEntries = data.timeEntries || [];
  const yr = String(year || new Date().getFullYear());
  const yearEntries = timeEntries.filter((e) => (e.date || "").startsWith(yr + "-"));
  return roundMoney(sum(yearEntries, (e) => Number(e.income) || 0));
}

/** Net = income − expenses, rounded to cents. */
export function netIncome(income, expenses) {
  return roundMoney((income || 0) - (expenses || 0));
}

function isClientDiscontinuedOn(client, dateKey) {
  if (!client?.is_discontinued) return false;
  if (!client.discontinued_on) return true;
  return dateKey >= client.discontinued_on;
}

/**
 * Weekly coverage alerts: for each active client, flag any of the last
 * `weeksBack` weeks where the client is missing a required weekly service
 * (Behavior Treatment OR Parent/Family Training).
 *
 * @param {Object} data - { clients, services, timeEntries, agencies }
 * @param {Date}   [anchorDate=new Date()]
 * @param {number} [weeksBack=4]
 * @returns {Array} alert objects
 */
export function buildCoverageAlerts(data, anchorDate = new Date(), weeksBack = 4) {
  const clients = data.clients || [];
  const services = data.services || [];
  const timeEntries = data.timeEntries || [];
  const agencies = data.agencies || [];
  const agenciesById = Object.fromEntries(agencies.map((a) => [a.id, a]));

  // Resolve the user's service ids that satisfy each required weekly service.
  const requiredIdsByName = {};
  for (const svc of services) {
    const canonical = REQUIRED_ALIASES[(svc.name || "").toLowerCase().trim()];
    if (canonical) {
      requiredIdsByName[canonical] = requiredIdsByName[canonical] || new Set();
      requiredIdsByName[canonical].add(svc.id);
    }
  }
  const required = REQUIRED_WEEKLY_SERVICES.map((name) => ({
    name,
    ids: requiredIdsByName[name] || new Set(),
  })).filter((r) => r.ids.size > 0);

  if (!required.length) return [];

  const currentWeekStart = startOfWeek(anchorDate);
  const weekStarts = Array.from({ length: weeksBack }, (_, i) =>
    addDays(currentWeekStart, -7 * (weeksBack - 1 - i))
  );

  return weekStarts.flatMap((weekStart) => {
    const weekStartKey = toDateKey(weekStart);
    const weekEndKey = toDateKey(addDays(weekStart, 6));
    return clients
      .filter((c) => !isClientDiscontinuedOn(c, weekEndKey))
      .flatMap((client) => {
        const clientEntries = timeEntries.filter(
          (e) => e.client_id === client.id && e.date >= weekStartKey && e.date <= weekEndKey
        );
        const missing = required
          .filter((req) => !clientEntries.some((e) => req.ids.has(e.service_id)))
          .map((req) => req.name);
        if (!missing.length) return [];
        return [
          {
            id: `${client.id}-${weekStartKey}-${missing.join("-")}`,
            clientId: client.id,
            clientInitials: client.initials,
            agencyName: agenciesById[client.agency_id]?.name || "No agency",
            weekStart: weekStartKey,
            weekEnd: weekEndKey,
            missingServices: missing,
          },
        ];
      });
  });
}

/**
 * The "aha" predicate (north-star). True once the user has ≥1 agency AND ≥1
 * payment cycle marked paid/unpaid — i.e. at least one reconciliation row OR a
 * derived cycle whose pay status is meaningful. We treat "≥1 derived cycle" as
 * the cycle existing, since a fresh entry against a payment-enabled agency
 * immediately yields an expected pay date.
 */
export function hasReachedAha(data, cycles) {
  const hasAgency = (data.agencies || []).length >= 1;
  const hasCycle = (cycles || []).length >= 1;
  return hasAgency && hasCycle;
}

/**
 * Expand a recurring expense into the list of date keys (YYYY-MM-DD) it should
 * occupy, from `startKey` through `untilKey` inclusive. Monthly steps clamp the
 * day to the target month's length (e.g. Jan 31 → Feb 28). `maxCount` is a hard
 * safety bound so a far-off `until` can't generate unbounded rows.
 *
 * @param {string} startKey   - first occurrence, YYYY-MM-DD
 * @param {string} frequency  - "none" | "daily" | "weekly" | "monthly"
 * @param {string} untilKey   - last allowed date, YYYY-MM-DD (inclusive)
 * @param {number} [maxCount=370]
 * @returns {string[]} occurrence date keys (always ≥ 1 when start is valid)
 */
export function recurringExpenseDates(startKey, frequency, untilKey, maxCount = 370) {
  if (!startKey) return [];
  if (!frequency || frequency === "none") return [startKey];
  const [sy, sm, sd] = startKey.split("-").map(Number);
  const until = untilKey
    ? (() => { const [y, m, d] = untilKey.split("-").map(Number); return new Date(y, m - 1, d); })()
    : null;
  const keyOf = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

  const dates = [];
  for (let i = 0; dates.length < maxCount; i++) {
    let dt;
    if (frequency === "daily") dt = new Date(sy, sm - 1, sd + i);
    else if (frequency === "weekly") dt = new Date(sy, sm - 1, sd + i * 7);
    else if (frequency === "monthly") {
      const total = (sm - 1) + i;
      const ty = sy + Math.floor(total / 12);
      const tm = ((total % 12) + 12) % 12;
      const lastDay = new Date(ty, tm + 1, 0).getDate();
      dt = new Date(ty, tm, Math.min(sd, lastDay));
    } else break;
    if (until && dt > until) break;
    dates.push(keyOf(dt));
    if (!until && i + 1 >= maxCount) break;
  }
  return dates;
}

/**
 * Client-level "you haven't logged this client this week" reminders. A configured
 * client (active, belonging to an agency) is expected to appear on the calendar
 * each week; when a week passes with no entries for that client, we surface a
 * dismissible reminder. Looks back `weeksBack` weeks including the current one.
 *
 * Guards against noise: the current (partial) week is only flagged from midweek
 * on; weeks before a client's first logged entry are skipped (so backfilled/
 * imported history is respected, not the row's created_at); and a client with no
 * history at all is only nudged for the current week.
 *
 * @param {Object} data - { clients, agencies, timeEntries }
 * @param {Date}   [anchorDate=new Date()]
 * @param {number} [weeksBack=2]
 * @returns {Array} alert objects { id, type, clientId, clientInitials, agencyName, weekStart, weekEnd, isCurrentWeek }
 */
export function buildMissingEntryAlerts(data, anchorDate = new Date(), weeksBack = 2) {
  const clients = data.clients || [];
  const agencies = data.agencies || [];
  const timeEntries = data.timeEntries || [];
  if (!clients.length || !agencies.length) return [];
  const agenciesById = Object.fromEntries(agencies.map((a) => [a.id, a]));

  // Earliest logged entry per client = when the working relationship effectively
  // started (handles imported/backfilled history that predates the row's created_at).
  const earliestByClient = {};
  for (const e of timeEntries) {
    if (!e.client_id || !e.date) continue;
    const cur = earliestByClient[e.client_id];
    if (!cur || e.date < cur) earliestByClient[e.client_id] = e.date;
  }

  const currentWeekStart = startOfWeek(anchorDate);
  const currentWeekKey = toDateKey(currentWeekStart);
  const dowMon = (anchorDate.getDay() + 6) % 7; // 0 = Monday

  const weekStarts = Array.from({ length: weeksBack }, (_, i) =>
    addDays(currentWeekStart, -7 * (weeksBack - 1 - i))
  );

  const out = [];
  for (const weekStart of weekStarts) {
    const wkStartKey = toDateKey(weekStart);
    const wkEndKey = toDateKey(addDays(weekStart, 6));
    const isCurrentWeek = wkStartKey === currentWeekKey;
    // Don't nag about the current week until midweek (Wed+) — give time to log.
    if (isCurrentWeek && dowMon < 2) continue;

    for (const client of clients) {
      if (isClientDiscontinuedOn(client, wkEndKey)) continue;
      const earliest = earliestByClient[client.id];
      if (earliest) {
        // Skip weeks before the client's first logged entry.
        if (wkEndKey < earliest) continue;
      } else if (!isCurrentWeek) {
        // No history yet — only nudge for the current week, not past weeks.
        continue;
      }

      const hasEntry = timeEntries.some(
        (e) => e.client_id === client.id && e.date >= wkStartKey && e.date <= wkEndKey
      );
      if (hasEntry) continue;

      out.push({
        id: `missing:${client.id}:${wkStartKey}`,
        type: "missing_entries",
        clientId: client.id,
        clientInitials: client.initials,
        agencyName: agenciesById[client.agency_id]?.name || null,
        weekStart: wkStartKey,
        weekEnd: wkEndKey,
        isCurrentWeek,
      });
    }
  }
  return out;
}
